import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, Lock, Mail, ArrowRight, Eye, EyeOff, Phone, User, Scan, Check } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../../utils/auth/authService';
import { biometricService } from '../../utils/security/biometric';
import '../../components/figma/src/index.css';

// Use the public logo
const whiteIcon = '/transparent-white-logo.png';

interface LoginScreenProps {
  onLoginSuccess: (user: any) => void;
}

type AuthMode = 'full-signin' | 'quick-login';
type QuickLoginMethod = 'biometric' | 'pin';
type BiometricType = 'face' | 'fingerprint';

export default function LoginScreen({ onLoginSuccess }: LoginScreenProps) {
  // Determine initial screen based on stored credentials
  const [savedUser, setSavedUser] = useState<any>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('full-signin');

  // Full sign-in form state
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);

  // Quick login state
  const [quickLoginMethod, setQuickLoginMethod] = useState<QuickLoginMethod>('biometric');
  const [biometricType, setBiometricType] = useState<BiometricType>('fingerprint');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [pin, setPin] = useState('');
  const [pinDots, setPinDots] = useState<boolean[]>([false, false, false, false]);

  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for saved credentials on mount
  useEffect(() => {
    const checkSavedCredentials = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('savedUser') || 'null');
        if (user) {
          setSavedUser(user);
          setAuthMode('quick-login');
        }

        // Check biometric availability
        const available = await biometricService.isAvailable();
        setBiometricAvailable(available);

        if (available) {
          const type = await biometricService.getBiometricType();
          setBiometricType(type as BiometricType);
        }
      } catch (error) {
        console.error('Error checking credentials:', error);
      }
    };

    checkSavedCredentials();
  }, []);

  // ==================== AUTHENTICATION HANDLERS ====================

  const handleFullSignIn = async () => {
    setError('');
    setIsLoading(true);

    try {
      const user = await authService.login(emailOrPhone, password, rememberDevice);

      if (user) {
        if (rememberDevice) {
          localStorage.setItem('savedUser', JSON.stringify({
            id: user.id,
            name: user.name || user.email,
            email: user.email,
            avatar: user.profilePhoto
          }));
        }

        toast.success('Welcome back!');
        onLoginSuccess(user);
      } else {
        throw new Error('Login failed');
      }
    } catch (error: any) {
      console.error('📱 LoginScreen: Login error:', error);
      
      // Check if this is a NavigatorX user trying to use the web app
      if (error.message?.startsWith('NAVIGATORX_USER:')) {
        const userName = error.message.split(':')[1] || 'there';
        setError(`NAVIGATORX_USER:${userName}`);
        toast.info(`Hi ${userName}! NavigatorX users need the mobile app.`);
      } else {
        const errorMessage = error.message || 'Login failed. Please check your credentials.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricScan = async () => {
    setIsScanning(true);
    setError('');

    try {
      if (!savedUser?.id) {
        throw new Error('User ID not found. Please sign in with password first.');
      }

      const result = await biometricService.authenticate();

      if (result.success) {
        setTimeout(() => {
          toast.success('Biometric authentication successful!');
          onLoginSuccess(savedUser);
        }, 1000);
      } else {
        throw new Error(result.error || 'Biometric authentication failed');
      }
    } catch (error: any) {
      console.error('Biometric auth error:', error);
      setError(error.message || 'Authentication failed');
      toast.error('Biometric authentication failed. Please try again.');
      setIsScanning(false);
    }
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);

      const newDots = [...pinDots];
      newDots[pin.length] = true;
      setPinDots(newDots);

      // Auto-submit when 4 digits entered
      if (newPin.length === 4) {
        handlePinSubmit(newPin);
      }
    }
  };

  const handlePinDelete = () => {
    if (pin.length > 0) {
      setPin(pin.slice(0, -1));
      const newDots = [...pinDots];
      newDots[pin.length - 1] = false;
      setPinDots(newDots);
    }
  };

  const handlePinSubmit = async (pinCode: string) => {
    setIsLoading(true);
    setError('');

    try {
      const user = await authService.verifyPin(pinCode);

      if (user) {
        toast.success('PIN verified successfully!');
        onLoginSuccess(user);
      } else {
        throw new Error('Invalid PIN');
      }
    } catch (error: any) {
      console.error('PIN verification error:', error);
      setError(error.message || 'Invalid PIN');
      toast.error('Invalid PIN. Please try again.');
      setPin('');
      setPinDots([false, false, false, false]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchUser = () => {
    localStorage.removeItem('savedUser');
    setSavedUser(null);
    setAuthMode('full-signin');
    setPin('');
    setPinDots([false, false, false, false]);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-slate-900 via-blue-900 to-slate-950">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 login-grid-background" />
      </div>

      {/* Floating Particles */}
      {[...Array(25)].map((_, i) => {
        const size = Math.random() * 3 + 1; // 1-4px varied sizes
        const glowIntensity = Math.random() * 10 + 5; // 5-15px glow

        return (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              background: 'radial-gradient(circle, rgba(96, 165, 250, 1) 0%, rgba(59, 130, 246, 0.8) 50%, rgba(96, 165, 250, 0.3) 100%)',
              boxShadow: `0 0 ${glowIntensity}px rgba(96, 165, 250, 0.8), 0 0 ${glowIntensity * 2}px rgba(59, 130, 246, 0.4)`,
              filter: `blur(${Math.random() * 0.5}px)`
            }}
            initial={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 400),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
              opacity: Math.random() * 0.7 + 0.3
            }}
            animate={{
              y: [null, Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800)],
              x: [null, Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 400)],
              opacity: [null, Math.random() * 0.7 + 0.3, Math.random() * 0.5 + 0.2, 0],
              scale: [1, Math.random() * 0.5 + 0.8, Math.random() * 1.2 + 0.6]
            }}
            transition={{
              duration: Math.random() * 20 + 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        );
      })}

      {/* Glowing orbs in background */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-blue-600 rounded-full blur-3xl opacity-20 animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-15 animate-pulse [animation-delay:1s]" />

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-screen">

        <AnimatePresence mode="wait">
          {authMode === 'full-signin' ? (
            // ==================== FULL SIGN-IN SCREEN ====================
            <motion.div
              key="full-signin"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="flex flex-col min-h-screen px-6 py-12"
            >
              {/* Logo & Branding */}
              <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col items-center mb-12"
              >
                {/* Large App Icon - No Background */}
                <motion.div
                  className="relative w-40 h-40 flex items-center justify-center mb-6"
                  whileHover={{ scale: 1.05 }}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", damping: 12 }}
                >
                  {/* Icon with subtle glow */}
                  <motion.img
                    src={whiteIcon}
                    alt="OilWellX"
                    className="w-full h-full object-contain"
                    animate={{
                      filter: [
                        'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))',
                        'drop-shadow(0 0 40px rgba(255, 255, 255, 0.5))',
                        'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))',
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                </motion.div>

                <motion.h1
                  className="text-white text-4xl tracking-tight mb-2 flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <span>OilWell</span>
                  <motion.span
                    className="relative inline-block font-extrabold text-6xl mx-1"
                    animate={{
                      scale: [1, 1.15, 1],
                      filter: [
                        'drop-shadow(0 0 15px rgba(96, 165, 250, 0.8)) drop-shadow(0 0 30px rgba(59, 130, 246, 0.6))',
                        'drop-shadow(0 0 25px rgba(96, 165, 250, 1)) drop-shadow(0 0 50px rgba(59, 130, 246, 0.8))',
                        'drop-shadow(0 0 15px rgba(96, 165, 250, 0.8)) drop-shadow(0 0 30px rgba(59, 130, 246, 0.6))',
                      ]
                    }}
                    transition={{
                      duration: 3.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #93c5fd 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      transformOrigin: 'center'
                    }}
                  >
                    X
                  </motion.span>
                </motion.h1>
                <p className="text-white/80">Oil Field Management Platform</p>
              </motion.div>

              {/* Sign In Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex-1 flex flex-col w-full max-w-md mx-auto"
              >
                <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6">
                  <h2 className="text-gray-900 text-2xl text-center mb-6">Sign In</h2>

                  <div className="space-y-4">
                    {/* Email or Phone Number */}
                    <div>
                      <label className="block text-gray-700 text-sm mb-2">
                        Email or Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={emailOrPhone}
                          onChange={(e) => setEmailOrPhone(e.target.value)}
                          className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter email or phone number"
                          aria-label="Email or Phone Number"
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-gray-700 text-sm mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 pl-12 pr-12 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter your password"
                          data-testid="login-password-input"
                        />
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Remember Device Checkbox */}
                    <motion.label
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={rememberDevice}
                          onChange={(e) => setRememberDevice(e.target.checked)}
                          className="sr-only"
                          aria-label="Remember this device"
                        />
                        <div className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                          ${rememberDevice
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-gray-300'
                          }
                        `}>
                          {rememberDevice && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </div>
                      </div>
                      <span className="text-gray-700 text-sm">
                        Remember this device (enable quick PIN login)
                      </span>
                    </motion.label>

                    {/* Sign In Button */}
                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={handleFullSignIn}
                      disabled={isLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="login-submit-button"
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </motion.button>

                    {/* Error Message */}
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-3 p-4 rounded-xl ${error.startsWith('NAVIGATORX_USER:') ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'}`}
                      >
                        {error.startsWith('NAVIGATORX_USER:') ? (
                          <div className="text-center space-y-3">
                            <div className="text-blue-700">
                              <p className="font-semibold text-base">👋 Hi {error.split(':')[1]}!</p>
                              <p className="text-sm mt-1">You're a <strong>NavigatorX</strong> user.</p>
                            </div>
                            <p className="text-blue-600 text-sm">
                              Please download the mobile app from the Google Play Store or Apple App Store.
                            </p>
                            <a 
                              href="https://oilwellx.com/download" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                              Download NavigatorX App
                            </a>
                          </div>
                        ) : (
                          <p className="text-red-700 text-sm">{error}</p>
                        )}
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center text-white/80 text-xs space-y-1">
                  <p>© 2026 OilWellX. All rights reserved.</p>
                  <div className="flex items-center justify-center gap-2 text-white/60">
                    <p>Powered by TGE Technology</p>
                    <span>•</span>
                    <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                    <span>•</span><span className="text-white/60">v2.1.420</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            // ==================== QUICK LOGIN SCREEN ====================
            <motion.div
              key="quick-login"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center min-h-screen px-6 py-8"
            >
              {/* Logo Area */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", damping: 15, stiffness: 100 }}
                className="mb-8"
              >
                {/* Large Icon - No Background */}
                <motion.div
                  className="relative w-40 h-40 flex items-center justify-center mb-6 mx-auto"
                >
                  {/* Icon with subtle glow and overlay effects */}
                  <motion.img
                    src={whiteIcon}
                    alt="OilWellX"
                    className="w-full h-full object-contain relative z-10"
                    animate={{
                      filter: [
                        'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))',
                        'drop-shadow(0 0 40px rgba(255, 255, 255, 0.5))',
                        'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))',
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />

                  {/* Animated White Oil Droplets flowing down */}
                  {[...Array(12)].map((_, i) => {
                    const randomSize = Math.random() * 2 + 1; // 1-3px
                    const randomX = (Math.random() - 0.5) * 30; // Spread across 30px width
                    const randomDuration = Math.random() * 2 + 2.5; // 2.5-4.5 seconds
                    const randomDelay = Math.random() * 3; // 0-3 second delay
                    const randomStartY = Math.random() * 15 + 10; // Start between 10-25% down
                    const irregularRadius = `${40 + Math.random() * 20}% ${50 + Math.random() * 20}% ${40 + Math.random() * 20}% ${50 + Math.random() * 20}%`;

                    return (
                      <motion.div
                        key={i}
                        className="absolute bg-white"
                        style={{
                          left: '50%',
                          top: `${randomStartY}%`,
                          width: `${randomSize}px`,
                          height: `${randomSize * 1.2}px`, // Slightly elongated
                          borderRadius: irregularRadius,
                          filter: 'blur(0.3px)',
                          boxShadow: '0 0 6px rgba(255, 255, 255, 0.6)',
                          transform: `translateX(${randomX}px)`
                        }}
                        animate={{
                          y: [0, 130],
                          x: [0, (Math.random() - 0.5) * 10], // Slight horizontal drift
                          opacity: [0, 0.8, 0.9, 0.7, 0],
                          scaleY: [1, 1.1, 1.2, 1, 0.8] // Droplet stretches as it falls
                        }}
                        transition={{
                          duration: randomDuration,
                          repeat: Infinity,
                          delay: randomDelay,
                          ease: [0.4, 0, 0.6, 1] // Custom easing for more natural fall
                        }}
                      />
                    );
                  })}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-center"
                >
                  <motion.h1
                    className="text-white text-4xl tracking-tight mb-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    <span>OilWell</span>
                    <motion.span
                      className="relative inline-block font-extrabold text-5xl"
                      animate={{
                        scale: [1, 1.2, 1],
                        textShadow: [
                          '0 0 30px rgba(96, 165, 250, 0.7), 0 0 60px rgba(59, 130, 246, 0.5)',
                          '0 0 50px rgba(96, 165, 250, 1), 0 0 100px rgba(59, 130, 246, 0.8)',
                          '0 0 30px rgba(96, 165, 250, 0.7), 0 0 60px rgba(59, 130, 246, 0.5)',
                        ]
                      }}
                      transition={{
                        duration: 4.5,
                        repeat: Infinity,
                        ease: [0.4, 0.0, 0.2, 1]
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #93c5fd 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundSize: '200% 200%',
                        animation: 'shimmer 3s ease-in-out infinite',
                        position: 'relative',
                        top: '3px',
                        left: '-2px',
                        marginLeft: '-8px',
                        transformOrigin: 'center center'
                      }}
                    >
                      X
                    </motion.span>
                  </motion.h1>
                  <p className="text-white/80">Welcome back, {savedUser?.name || 'User'}</p>
                </motion.div>
              </motion.div>

              {/* Quick Login Method Switcher */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex gap-3 mb-6"
              >
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setQuickLoginMethod('biometric')}
                  className={`px-6 py-3 rounded-2xl transition-all flex items-center gap-2 ${quickLoginMethod === 'biometric'
                      ? 'bg-white text-blue-700'
                      : 'bg-white/10 text-white backdrop-blur'
                    }`}
                >
                  {biometricType === 'face' ? <Scan className="w-4 h-4" /> : <Fingerprint className="w-4 h-4" />}
                  <span>{biometricType === 'face' ? 'Face ID' : 'Fingerprint'}</span>
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setQuickLoginMethod('pin')}
                  className={`px-6 py-3 rounded-2xl transition-all flex items-center gap-2 ${quickLoginMethod === 'pin'
                      ? 'bg-white text-blue-700'
                      : 'bg-white/10 text-white backdrop-blur'
                    }`}
                >
                  <Lock className="w-4 h-4" />
                  <span>PIN</span>
                </motion.button>
              </motion.div>

              {/* Auth Interface */}
              <AnimatePresence mode="wait">
                {quickLoginMethod === 'biometric' ? (
                  <motion.div
                    key="biometric"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    className="w-full max-w-sm"
                  >
                    {/* Biometric Scanner */}
                    <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-[2.5rem] p-6 relative overflow-hidden">
                      {/* Scan Animation */}
                      {isScanning && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-transparent"
                          animate={{ y: ['0%', '100%'] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        />
                      )}

                      <div className="relative z-10 flex flex-col items-center">
                        {/* Biometric Icon */}
                        <motion.div
                          className="relative mb-4"
                          animate={{ scale: isScanning ? [1, 1.05, 1] : 1 }}
                          transition={{ duration: 0.5, repeat: isScanning ? Infinity : 0 }}
                        >
                          <motion.div
                            className="w-28 h-28 rounded-full bg-gradient-to-br from-white to-blue-100 flex items-center justify-center shadow-2xl"
                            animate={{
                              boxShadow: isScanning
                                ? [
                                  '0 0 0 0 rgba(255, 255, 255, 0.7)',
                                  '0 0 0 30px rgba(255, 255, 255, 0)',
                                  '0 0 0 0 rgba(255, 255, 255, 0)'
                                ]
                                : '0 10px 40px rgba(0, 0, 0, 0.2)'
                            }}
                            transition={{ duration: 1.5, repeat: isScanning ? Infinity : 0 }}
                          >
                            {biometricType === 'face' ? (
                              <Scan className="w-14 h-14 text-blue-600" />
                            ) : (
                              <Fingerprint className="w-14 h-14 text-blue-600" />
                            )}
                          </motion.div>
                        </motion.div>

                        <h3 className="text-white text-lg mb-1">
                          {isScanning ? 'Authenticating...' : 'Secure Access'}
                        </h3>
                        <p className="text-blue-100 text-sm text-center mb-4">
                          {isScanning
                            ? 'Verifying your identity'
                            : `Use ${biometricType === 'face' ? 'Face ID' : 'fingerprint'} to unlock`
                          }
                        </p>

                        {!isScanning && (
                          <>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={handleBiometricScan}
                              className="w-full bg-white text-blue-700 py-3.5 rounded-2xl flex items-center justify-center gap-2 mb-3"
                            >
                              <span>Scan to Continue</span>
                              <ArrowRight className="w-5 h-5" />
                            </motion.button>

                            {/* Switch Biometric Type */}
                            <button
                              onClick={() => setBiometricType(biometricType === 'face' ? 'fingerprint' : 'face')}
                              className="text-white/80 text-xs hover:text-white transition-colors"
                            >
                              Use {biometricType === 'face' ? 'Fingerprint' : 'Face ID'} instead
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="pin"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    className="w-full max-w-sm"
                  >
                    {/* PIN Entry */}
                    <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-[3rem] p-8">
                      <div className="flex flex-col items-center">
                        <h3 className="text-white text-xl mb-2">Enter PIN</h3>
                        <p className="text-blue-100 text-sm mb-8">Enter your 4-digit PIN</p>

                        {/* PIN Dots Display */}
                        <div className="flex gap-4 mb-12">
                          {pinDots.map((filled, i) => (
                            <motion.div
                              key={i}
                              animate={{
                                scale: filled ? [1, 1.2, 1] : 1,
                                backgroundColor: filled ? '#ffffff' : 'rgba(255, 255, 255, 0.3)'
                              }}
                              className="w-4 h-4 rounded-full"
                            />
                          ))}
                        </div>

                        {/* Number Pad */}
                        <div className="grid grid-cols-3 gap-4 w-full max-w-xs">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                            <motion.button
                              key={num}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handlePinInput(num.toString())}
                              className="aspect-square bg-white/20 hover:bg-white/30 rounded-2xl text-white text-2xl transition-colors"
                            >
                              {num}
                            </motion.button>
                          ))}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setQuickLoginMethod('biometric')}
                            className="aspect-square bg-white/10 hover:bg-white/20 rounded-2xl text-white text-xs transition-colors flex items-center justify-center"
                          >
                            {biometricType === 'face' ? <Scan className="w-6 h-6" /> : <Fingerprint className="w-6 h-6" />}
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handlePinInput('0')}
                            className="aspect-square bg-white/20 hover:bg-white/30 rounded-2xl text-white text-2xl transition-colors"
                          >
                            0
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handlePinDelete}
                            className="aspect-square bg-white/10 hover:bg-white/20 rounded-2xl text-white text-xs transition-colors flex items-center justify-center"
                          >
                            ← DEL
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Switch to Full Sign-in */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                onClick={handleSwitchUser}
                className="mt-8 text-white/70 text-sm hover:text-white transition-colors"
              >
                Not you? Sign in with different account
              </motion.button>

              {/* Footer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="mt-6 text-white/50 text-xs flex flex-col items-center gap-1"
              >
                <div className="flex items-center gap-2">
                  <p>Powered by TGE Technology</p>
                  <span>•</span>
                  <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                  <span>•</span><span className="text-white/60">v2.1.420</span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}



















































































































































































































































